import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AssistantConversations.css';

const LIMIT = 50;

const formatRelative = (value) => {
  if (!value) return 'never';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'unknown';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const formatAbsolute = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUserName = (u) => {
  const parts = [u.name, u.surname].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Anonymous';
};

const AssistantUsers = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(LIMIT));
      if (search) params.set('search', search);
      const resp = await fetchWithAuth(`/api/admin/assistant/users?${params.toString()}`);
      if (!resp.ok) throw new Error('Failed to load users');
      const data = await resp.json();
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>AI Chats — Users</h1>

      <div className="card">
        <div className="assistant-filter-bar">
          <input
            type="text"
            placeholder="Search by name or email..."
            className="input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <span style={{ color: '#666', fontSize: 14, marginLeft: 'auto' }}>
            {total.toLocaleString()} users with chats
          </span>
        </div>

        {error && <div className="error" style={{ padding: 12, color: '#b91c1c' }}>Error: {error}</div>}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th style={{ width: 140 }}>Conversations</th>
                <th style={{ width: 200 }}>Last chatted</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 24, color: '#666' }}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: 24, color: '#666' }}>
                  No users {search ? 'match this search' : 'with chats yet'}.
                </td></tr>
              ) : (
                items.map((u) => (
                  <tr
                    key={u.id}
                    className="assistant-row-clickable"
                    onClick={() => navigate(`/assistant?userId=${u.id}`)}
                  >
                    <td>{formatUserName(u)}</td>
                    <td>{u.email || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td>{u.conversationCount.toLocaleString()}</td>
                    <td title={formatAbsolute(u.lastChattedAt)}>{formatRelative(u.lastChattedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page - 1)}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <span className="page-info">Page {page} of {totalPages}</span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantUsers;
